import { useEffect, useRef, useState } from 'react';
import { resolveActivationLandingState } from '../activationLandingState';

const ACTIVATION_ENDPOINT =
  'https://europe-west1-pakmazar-prod.cloudfunctions.net/activateAccount';

// Customer-facing copy, in the customer's own language (from ?lang=,
// resolved by resolveActivationLandingState) -- this page is reached by a
// customer clicking an emailed link, never a logged-in admin, so it must
// not depend on the admin panel's own useLang()/LangContext (a different,
// admin-preference-driven concern, and not populated for a first-time
// visitor here anyway).
const COPY = {
  az: {
    readyTitle: 'Hesabınızı aktivləşdirin',
    readyBody:
      'Hesabınız təsdiqlənib. Aktivasiyanı tamamlamaq üçün aşağıdakı ' +
      'düyməyə toxunun.',
    readyButton: 'Hesabı aktivləşdir',
    submittingBody: 'Hesabınız aktivləşdirilir...',
    successTitle: 'Hesab aktivləşdirildi',
    successBody:
      'PakMazar hesabınız artıq aktivdir. PakMazar tətbiqinə qayıdıb ' +
      'daxil ola bilərsiniz.',
    likelyVerifiedTitle: 'E-poçt təsdiqləndi',
    likelyVerifiedBody:
      'E-poçt ünvanınız təsdiqləndi. PakMazar tətbiqinə qayıdın -- ' +
      'tətbiq açıqdırsa, hesabınız avtomatik aktivləşəcək.',
    invalidTitle: 'Keçid etibarsızdır',
    invalidBody:
      'Bu aktivasiya keçidi etibarsızdır, vaxtı bitib və ya artıq ' +
      'istifadə olunub. Zəhmət olmasa PakMazar dəstəyi ilə əlaqə saxlayın ' +
      'və ya yeni aktivasiya məktubu tələb edin.',
    errorTitle: 'Aktivləşdirmək mümkün olmadı',
    errorBody:
      'Hazırda aktivləşdirməni tamamlamaq mümkün olmadı. Zəhmət olmasa ' +
      'yeni aktivasiya məktubu ilə yenidən cəhd edin.',
  },
  ru: {
    readyTitle: 'Активируйте аккаунт',
    readyBody:
      'Ваш аккаунт одобрен. Нажмите кнопку ниже, чтобы завершить ' +
      'активацию.',
    readyButton: 'Активировать аккаунт',
    submittingBody: 'Активация аккаунта...',
    successTitle: 'Аккаунт активирован',
    successBody:
      'Ваш аккаунт PakMazar теперь активен. Вы можете вернуться в ' +
      'приложение PakMazar и войти.',
    likelyVerifiedTitle: 'Email подтверждён',
    likelyVerifiedBody:
      'Ваш email подтверждён. Вернитесь в приложение PakMazar -- если ' +
      'оно уже открыто, ваш аккаунт активируется автоматически.',
    invalidTitle: 'Ссылка недействительна',
    invalidBody:
      'Эта ссылка активации недействительна, истекла или уже была ' +
      'использована. Пожалуйста, свяжитесь со службой поддержки ' +
      'PakMazar или запросите новое письмо активации.',
    errorTitle: 'Не удалось активировать',
    errorBody:
      'Сейчас не удалось завершить активацию. Пожалуйста, попробуйте ' +
      'снова с новым письмом активации.',
  },
  en: {
    readyTitle: 'Activate your account',
    readyBody:
      'Your account has been approved. Tap the button below to finish ' +
      'activating it.',
    readyButton: 'Activate account',
    submittingBody: 'Activating your account...',
    successTitle: 'Account activated',
    successBody:
      'Your PakMazar account is now active. You can return to the ' +
      'PakMazar app and sign in.',
    likelyVerifiedTitle: 'Email confirmed',
    likelyVerifiedBody:
      'Your email has been confirmed. Return to the PakMazar app -- if ' +
      "it's already open, your account will finish activating " +
      'automatically.',
    invalidTitle: 'Link unavailable',
    invalidBody:
      'This activation link is invalid, expired, or has already been ' +
      'used. Please contact PakMazar support or request a new ' +
      'activation email.',
    errorTitle: 'Unable to activate',
    errorBody:
      'We could not complete activation right now. Please try again ' +
      'with a new activation email.',
  },
};

export default function ActivateAccountPage() {
  const tokenRef = useRef('');
  const [state, setState] = useState('loading');
  const [lang, setLang] = useState('az');

  useEffect(() => {
    const resolved = resolveActivationLandingState(
      window.location.hash,
      window.location.search
    );

    setLang(resolved.lang);

    // Remove the bearer token (legacy mechanism) from the visible browser
    // URL immediately -- harmless no-op for the current mechanism, which
    // never puts anything sensitive in the URL to begin with.
    window.history.replaceState(
      null,
      document.title,
      window.location.pathname
    );

    if (resolved.kind === 'legacy-token') {
      tokenRef.current = resolved.token;
      setState('ready');
      return;
    }

    // CURRENT mechanism: reaching this page with no legacy token is only
    // possible after Firebase's own hosted action page already verified
    // the email successfully -- see resolveActivationLandingState's own
    // doc comment for why. This is the expected, successful outcome for a
    // brand-new activation link, not a missing/invalid one.
    tokenRef.current = '';
    setState('likely-verified');
  }, []);

  const activate = async () => {
    const token = tokenRef.current;

    if (!token || state !== 'ready') {
      setState('invalid');
      return;
    }

    setState('submitting');

    try {
      const response = await fetch(ACTIVATION_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      // Remove the plaintext token from application memory after the request.
      tokenRef.current = '';

      if (!response.ok) {
        setState('invalid');
        return;
      }

      setState('success');
    } catch (_) {
      tokenRef.current = '';
      setState('error');
    }
  };

  const copy = COPY[lang] || COPY.az;

  if (state === 'loading') {
    return (
      <main className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <section className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
        <h1 className="text-2xl font-bold text-gray-900">
          PakMazar
        </h1>

        {state === 'ready' && (
          <>
            <h2 className="mt-6 text-xl font-semibold text-gray-900">
              {copy.readyTitle}
            </h2>
            <p className="mt-3 text-sm text-gray-600">
              {copy.readyBody}
            </p>
            <button
              type="button"
              onClick={activate}
              className="mt-6 w-full rounded-xl bg-primary px-4 py-3 font-semibold text-white"
            >
              {copy.readyButton}
            </button>
          </>
        )}

        {state === 'submitting' && (
          <>
            <div className="mx-auto mt-6 animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
            <p className="mt-4 text-sm text-gray-600">
              {copy.submittingBody}
            </p>
          </>
        )}

        {state === 'success' && (
          <>
            <h2 className="mt-6 text-xl font-semibold text-gray-900">
              {copy.successTitle}
            </h2>
            <p className="mt-3 text-sm text-gray-600">
              {copy.successBody}
            </p>
          </>
        )}

        {state === 'likely-verified' && (
          <>
            <h2 className="mt-6 text-xl font-semibold text-gray-900">
              {copy.likelyVerifiedTitle}
            </h2>
            <p className="mt-3 text-sm text-gray-600">
              {copy.likelyVerifiedBody}
            </p>
          </>
        )}

        {state === 'invalid' && (
          <>
            <h2 className="mt-6 text-xl font-semibold text-gray-900">
              {copy.invalidTitle}
            </h2>
            <p className="mt-3 text-sm text-gray-600">
              {copy.invalidBody}
            </p>
          </>
        )}

        {state === 'error' && (
          <>
            <h2 className="mt-6 text-xl font-semibold text-gray-900">
              {copy.errorTitle}
            </h2>
            <p className="mt-3 text-sm text-gray-600">
              {copy.errorBody}
            </p>
          </>
        )}
      </section>
    </main>
  );
}
